import React, { useEffect } from 'react';

const SmoothScroll = ({ children }) => {
  useEffect(() => {
    // Enhanced smooth scrolling behavior
    const enableSmoothScrolling = () => {
      // Set smooth scrolling for the entire document
      document.documentElement.style.scrollBehavior = 'smooth';
      
      // Add smooth scrolling for all scrollable containers
      const scrollableElements = document.querySelectorAll('.scroll-smooth, [data-smooth-scroll]');
      scrollableElements.forEach(element => {
        element.style.scrollBehavior = 'smooth';
        element.style.webkitOverflowScrolling = 'touch';
      });

      // Enhanced scroll animations
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }
        });
      }, observerOptions);

      // Observe elements with scroll-animate class
      const animatedElements = document.querySelectorAll('.scroll-animate');
      animatedElements.forEach(el => {
        el.style.opacity = '0';
        el.style.transform = 'translateY(30px)';
        el.style.transition = 'all 0.6s ease-out';
        observer.observe(el);
      });

      // Smooth scroll to anchor links
      const handleAnchorClick = (e) => {
        const target = e.target.closest('a[href^="#"]');
        if (target) {
          const targetId = target.getAttribute('href') || '';

          // Hash-style app routes are not valid CSS selectors (e.g. "#/product/...").
          // Skip smooth-scroll handling and avoid runtime errors.
          if (targetId.startsWith('#/')) {
            e.preventDefault();
            console.info('Parameter text not available; skipping anchor scroll:', targetId);
            return;
          }

          e.preventDefault();
          let targetElement = null;
          try {
            targetElement = document.querySelector(targetId);
          } catch (err) {
            console.info('Parameter text not available; invalid anchor selector:', targetId);
            return;
          }

          if (targetElement) {
            const headerHeight = document.querySelector('header')?.offsetHeight || 0;
            const targetPosition = targetElement.offsetTop - headerHeight - 20;

            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          } else {
            console.info('Parameter text not available; anchor target missing:', targetId);
          }
        }
      };

      document.addEventListener('click', handleAnchorClick);

      // Enhanced scroll performance
      let ticking = false;
      const handleScroll = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            // Add scroll-based animations
            const scrolled = window.pageYOffset;
            const parallaxElements = document.querySelectorAll('[data-parallax]');
            
            parallaxElements.forEach(element => {
              const speed = element.getAttribute('data-parallax') || 0.5;
              const yPos = -(scrolled * speed);
              element.style.transform = `translateY(${yPos}px)`;
            });

            // Add scroll-triggered animations
            const scrollElements = document.querySelectorAll('.scroll-trigger');
            scrollElements.forEach(element => {
              const elementTop = element.offsetTop;
              const elementHeight = element.offsetHeight;
              const scrollTop = window.pageYOffset;
              const windowHeight = window.innerHeight;
              
              if (scrollTop + windowHeight > elementTop && scrollTop < elementTop + elementHeight) {
                element.classList.add('animate-fade-in-up');
              }
            });

            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: true });

      // Cleanup function
      return () => {
        document.removeEventListener('click', handleAnchorClick);
        window.removeEventListener('scroll', handleScroll);
        observer.disconnect();
      };
    };

    // Initialize smooth scrolling
    const cleanup = enableSmoothScrolling();

    return cleanup;
  }, []);

  return <>{children}</>;
};

export default SmoothScroll;
